import { Sentry } from 'lib-shared-sentry';
import type { AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux';

import { conversationsApi } from 'apis/conversations/conversations.api';
import type { AppDispatch, AppState } from 'bootstrap.store';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import * as telephonyActions from '../../../../../components/telephony/actions/telephony';
import { TwilioRuntime } from '../runtime';
import type { TokenResponse, TwilioTokenRegion } from '../types';

import { createActionHandlers } from './actionHandlers';
import { createTwilioCommandExecutor } from './commandExecutor';
import { buildEventHandlers } from './eventHandlers';
import { createRegionResolver } from './regionResolver';

const { telephonyRenewToken, telephonyGetAllOwnNumbers } =
  telephonyActions.actions;

/**
 * Voice SDK implementation of the Twilio middleware.
 *
 * Owns all Redux concerns: maps action types to TwilioRuntime commands and maps
 * TwilioRuntime promise results back to Redux action dispatches. TwilioRuntime
 * itself has no knowledge of Redux.
 *
 * Region management lives here — the twilioRuntime only sees a no-arg getToken
 * that returns a token with the correct region already encoded in it.
 */
export const voiceSdkTwilioMiddleware =
  (): Middleware<unknown, AppState> =>
  (store: MiddlewareAPI<Dispatch<AnyAction>, AppState>) => {
    const appDispatch = store.dispatch as AppDispatch;
    const fetchToken = bindActionToPromise<
      { region: TwilioTokenRegion },
      TokenResponse
    >(store.dispatch, telephonyRenewToken);

    const fetchOwnNumbers = bindActionToPromise<
      Record<string, unknown>,
      unknown
    >(store.dispatch, telephonyGetAllOwnNumbers);

    const dispatch = store.dispatch;

    // ── Region state ──────────────────────────────────────────────────────────

    let currentRegion: TwilioTokenRegion | undefined;

    const getToken = () =>
      fetchToken(currentRegion != null ? { region: currentRegion } : undefined);

    // ── Runtime ───────────────────────────────────────────────────────────────

    const handlers = buildEventHandlers(dispatch);
    const twilioRuntime = new TwilioRuntime(getToken, handlers);

    // ── Region management ─────────────────────────────────────────────────────

    // Async queue that ensures region switches run one at a time.
    //
    // Each call to ensureDeviceRegion() chains its work onto the tail of this
    // promise so it only starts after the previous switch has fully settled.
    // This prevents a second call from mutating `currentRegion` while an
    // in-flight initializeDevice() is still reading it via the getToken closure.
    //
    // Starts as Promise.resolve() so the first call has something to chain on
    // and begins immediately with no waiting.
    let pendingRegionSwitch: Promise<void> = Promise.resolve();

    const ensureDeviceRegion = (region: TwilioTokenRegion): Promise<void> => {
      const next = pendingRegionSwitch.then(async () => {
        if (currentRegion === region) {
          return;
        }

        currentRegion = region;
        await twilioRuntime.initializeDevice();
      });

      pendingRegionSwitch = next.catch(() => {});
      return next;
    };

    const region = createRegionResolver({
      getState: store.getState,
      fetchOwnNumbers,
      readConversation: async (csid: number) => {
        const request = appDispatch(
          conversationsApi.endpoints.readConversation.initiate({ csid }),
        );

        try {
          return await request.unwrap();
        } finally {
          request.unsubscribe();
        }
      },
    });

    // ── Command execution ─────────────────────────────────────────────────────

    const executor = createTwilioCommandExecutor({
      dispatch,
      ensureDeviceRegion,
    });

    // ── Action routing ────────────────────────────────────────────────────────

    const actionHandlers = createActionHandlers({
      dispatch,
      runtime: twilioRuntime,
      handlers,
      executor,
      region,
      resetRegion: () => {
        currentRegion = undefined;
      },
    });

    const isTwilioAction = (action: AnyAction) =>
      Boolean(action.type && (action.type as string).includes('TWILIO'));

    return (next: Dispatch<AnyAction>) =>
      (action: AnyAction): AnyAction => {
        if (!isTwilioAction(action)) {
          return next(action);
        }

        // Let reducers observe the command action first so any immediate
        // validation failure can still terminate the loading state with a
        // subsequent *Failed action.
        const result = next(action);

        try {
          actionHandlers[action.type]?.(action);
        } catch (exception) {
          Sentry.captureException(exception);

          try {
            action.meta?.resolve?.(undefined);
          } catch {
            // meta.resolve may not exist for non-promisified actions
          }
        }

        return result;
      };
  };
